import {
  IrisBankAccount,
  IrisBalance,
  IrisConsent,
  IrisTransaction,
  IrisConsentStatus,
  IrisCreditDebitIndicator,
} from '@app/core/types/iris-report';
import { Decimal } from '@app/core/utils/decimal';
import moment from 'moment';

export class BankAccountView {
  /**
   * Priority
   */
  private static readonly BALANCE_PRIORITY: string[] = [
    'interimavailable',
    'expected',
    'authorised',
    'forwardavailable',
    'information',
    'closingbooked',
    'openingbooked',
  ];

  account: IrisBankAccount;
  brand: string;
  balance: number | null;
  balanceCurrency: string;
  balanceReferenceDate: string | null;
  balances: IrisBalance[];
  consent: IrisConsent | null;
  consentStatusColor!: string;
  consentStatusLabel!: string;
  transactions: IrisTransaction[];
  totalIncoming31Days: number;
  totalOutgoing31Days: number;
  expanded: boolean;
  isDifferentBrand: boolean;

  constructor(
    account: IrisBankAccount,
    brand: string,
    clientBrandCode: string
  ) {
    this.account = account;
    this.brand = brand;
    this.isDifferentBrand = brand !== clientBrandCode;

    this.consent =
      account.consents?.find(
        (c) =>
          c.status === IrisConsentStatus.VALID ||
          c.status === IrisConsentStatus.AUTHORISED
      ) ||
      account.consents?.[0] ||
      null;

    this.resolveConsentDisplay();

    const bestBalance = this.findBestBalance();

    this.balance = bestBalance?.amount ?? null;
    this.balanceCurrency = bestBalance?.currency ?? account.currency;
    this.balanceReferenceDate = bestBalance?.referenceDate ?? null;
    this.balances = account.balances || [];
    this.transactions = account.transactions || [];
    this.expanded = false;

    const { totalIncoming, totalOutgoing } = this.calculateTurnover();
    this.totalIncoming31Days = totalIncoming;
    this.totalOutgoing31Days = totalOutgoing;
  }

  private findBestBalance(): IrisBalance | undefined {
    if (!this.account.balances?.length) return undefined;

    for (const type of BankAccountView.BALANCE_PRIORITY) {
      const matchingBalances = this.account.balances.filter(
        (b) => b.balanceType.toLowerCase() === type
      );

      if (matchingBalances.length > 0) {
        return matchingBalances.sort((a, b) =>
          moment(b.referenceDate).diff(moment(a.referenceDate))
        )[0];
      }
    }
    return this.account.balances.slice().sort((a, b) =>
      moment(b.referenceDate).diff(moment(a.referenceDate))
    )[0];
  }

  private resolveConsentDisplay(): void {
    if (!this.consent) {
      this.consentStatusColor = 'warn';
      this.consentStatusLabel = 'bankInfo.consent.noConsent';
      return;
    }

    const statusMap: { [key: string]: { color: string; label: string } } = {
      [IrisConsentStatus.VALID]: {
        color: 'accent',
        label: 'bankInfo.consent.statusValid',
      },
      [IrisConsentStatus.AUTHORISED]: {
        color: 'accent',
        label: 'bankInfo.consent.statusAuthorised',
      },
      [IrisConsentStatus.RECEIVED]: {
        color: 'primary',
        label: 'bankInfo.consent.statusReceived',
      },
      [IrisConsentStatus.REJECTED]: {
        color: 'warn',
        label: 'bankInfo.consent.statusRejected',
      },
      [IrisConsentStatus.EXPIRED]: {
        color: 'warn',
        label: 'bankInfo.consent.statusExpired',
      },
      [IrisConsentStatus.REVOKED_BY_PSU]: {
        color: 'warn',
        label: 'bankInfo.consent.statusRevoked',
      },
      [IrisConsentStatus.TERMINATED_BY_TPP]: {
        color: 'warn',
        label: 'bankInfo.consent.statusTerminated',
      },
      [IrisConsentStatus.PARTIALLY_AUTHORISED]: {
        color: 'primary',
        label: 'bankInfo.consent.statusPartial',
      },
    };

    const config = statusMap[this.consent.status] || {
      color: 'warn',
      label: 'bankInfo.consent.statusUnknown',
    };
    this.consentStatusColor = config.color;
    this.consentStatusLabel = config.label;
  }

  private calculateTurnover(): {
    totalIncoming: number;
    totalOutgoing: number;
  } {
    const now = new Date();
    const thirtyOneDaysAgo = new Date(now.getTime() - 31 * 24 * 60 * 60 * 1000);

    const incomingAmounts: number[] = [];
    const outgoingAmounts: number[] = [];

    for (const txn of this.transactions) {
      const txnDate = new Date(txn.bookingDate);
      if (txnDate >= thirtyOneDaysAgo) {
        const amount = Math.abs(txn.transactionAmount.amount);
        if (txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT) {
          incomingAmounts.push(amount);
        } else {
          outgoingAmounts.push(amount);
        }
      }
    }

    return {
      totalIncoming: Decimal.sumNumbers(...incomingAmounts),
      totalOutgoing: Decimal.sumNumbers(...outgoingAmounts),
    };
  }

  get turnover(): number {
    return Decimal.sumNumbers(
      this.totalIncoming31Days,
      this.totalOutgoing31Days
    );
  }
}